import { useCallback } from 'react';

import { formatDiscountForDisplay } from 'shared/helpers/specials';
import { Product } from 'utils/helpers/product';

import {
  calcDiscountPercentage,
  getIsSpecialApplicable,
  getProductSpecialsKeys,
  SpecialRestriction,
  transformSpecialRestrictions,
} from './specials.utils';

import { formatPrice, formatDiscount, majorityCalculationMethod } from './pricing.utils';

import { UsePriceOptionsProps, UsePriceOptionsReturn, PriceOption, Special } from './use-product-pricing.types';

type PriceOptionFilterProps = Pick<
  Product,
  'limitsPerCustomer' | 'manualInventory' | 'optionsBelowKioskThreshold' | 'optionsBelowThreshold'
> & {
  isKiosk: boolean;
};

type PriceOptionFilter = (option: PriceOption) => boolean;

type TransformOptionsProps = {
  options: Product['Options'];
  prices: Product['medicalPrices'] | Product['recPrices'];
  product: Product;
  specialPrices: Product['medicalSpecialPrices'] | Product['recSpecialPrices'];
  specialRestrictions: SpecialRestriction[];
  priceOptionFilter: PriceOptionFilter;
  // True only when the server's applyPOSPrices actually overwrote this product/menuType's price
  // arrays this request (product.posSyncedPricingApplied, stamped server-side -- not inferred from
  // POSPricingData bucket presence, which can be stale). When true, `special` is decided purely by
  // the specialPrice-vs-price diff -- legacy specialData restrictions are irrelevant for POS-synced
  // discounts (they have no saleSpecials entry at all) and are ignored rather than OR'd in, so this
  // can't widen scope for non-POS-synced products. When false, behavior is exactly the original
  // getIsSpecialApplicable-only check.
  usesPosSyncedPricing: boolean;
  // The majority calculation method (see majorityCalculationMethod in pricing.utils.ts) across this
  // menuType's POSPricingData bucket.discounts -- only meaningful when usesPosSyncedPricing is true.
  // 'AMOUNT' formats as "$X off"; 'PERCENT' or unresolved/null falls back to "X% off". Legacy
  // (non-POS-synced) discounts ignore this entirely.
  posCalculationMethod: 'AMOUNT' | 'PERCENT' | null;
};

function isArrayOfString(value: any): value is Array<string> {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

function isArrayOfNumber(value: any): value is Array<number> {
  return Array.isArray(value) && value.every((item) => typeof item === 'number');
}

function getLimitPerCustomerPerOption(
  limitsPerCustomer: Product['limitsPerCustomer'],
  optionValue: string
): number | null {
  if (limitsPerCustomer && Array.isArray(limitsPerCustomer)) {
    const optionLimit = limitsPerCustomer.find((item) => item?.key === optionValue);

    if (optionLimit?.value && optionLimit.value !== 'Unlimited') {
      return parseInt(optionLimit.value, 10);
    }
  }

  return null;
}

function getSpecial(specialPrice: number, price: number): Special {
  const discount = calcDiscountPercentage(specialPrice, price);

  return {
    price: specialPrice,
    formattedPrice: formatPrice(specialPrice),
    discount,
    formattedDiscount: formatDiscount(discount),
  };
}

function transformOptions({
  options,
  prices,
  product,
  specialPrices,
  specialRestrictions,
  priceOptionFilter,
  usesPosSyncedPricing,
  posCalculationMethod,
}: TransformOptionsProps): PriceOption[] {
  if (!isArrayOfString(options) || !isArrayOfNumber(prices) || !isArrayOfNumber(specialPrices)) {
    return [];
  }

  const uniqPriceOptions: Record<string, PriceOption> = {};

  options.forEach((value, idx) => {
    const price = prices[idx];
    const specialPrice = specialPrices[idx];
    const hasSpecialPriceDiff = Boolean(specialPrice) && specialPrice < price;
    const isSpecial = usesPosSyncedPricing ? hasSpecialPriceDiff : getIsSpecialApplicable(value, specialRestrictions);

    const label = value.replace(`oz`, ` oz`).replace(`gC`, `g`);
    const uniqKey = value.replace('C', '');

    uniqPriceOptions[uniqKey] = {
      value,
      label,
      price,
      formattedDiscount: formatDiscountForDisplay(
        price,
        specialPrice,
        product,
        usesPosSyncedPricing ? 'sale' : null,
        value,
        undefined,
        usesPosSyncedPricing && posCalculationMethod === 'AMOUNT'
      ),
      formattedPrice: formatPrice(price),
      special: specialPrice && isSpecial ? getSpecial(specialPrice, price) : null,
    };
  });

  return Object.values(uniqPriceOptions).filter(priceOptionFilter);
}

function getPriceOptionFilter({
  isKiosk,
  limitsPerCustomer,
  optionsBelowThreshold,
  optionsBelowKioskThreshold,
  manualInventory,
}: PriceOptionFilterProps): PriceOptionFilter {
  return ({ value }) => {
    if (getLimitPerCustomerPerOption(limitsPerCustomer, value) === 0) {
      return false;
    }

    if (!isKiosk && optionsBelowThreshold?.includes(value)) {
      return false;
    }

    if (isKiosk && optionsBelowKioskThreshold?.includes(value)) {
      return false;
    }

    if (manualInventory?.length) {
      const inventoryOption = manualInventory.find((item) => item?.option === value);

      if (inventoryOption?.inventory) {
        return inventoryOption.inventory > 0;
      }
    }

    return true;
  };
}

/*
  This is a refactor of:
    - src/utils/helpers/product.js: groupOptionsWithPrices
    - shared/helpers/products.js: formatWeightOptions

  TODO:
   - implement `showQualifyingOptions` when needed
   - implement `specialId` when needed
   - maybe memoaize results
*/
export function usePriceOptions({ menuType, isKiosk }: UsePriceOptionsProps): UsePriceOptionsReturn {
  const getPriceOptions = useCallback(
    (product: Product) => {
      const {
        limitsPerCustomer,
        manualInventory,
        medicalPrices,
        medicalSpecialPrices,
        Options: options,
        optionsBelowKioskThreshold,
        optionsBelowThreshold,
        POSPricingData,
        posSyncedPricingApplied,
        recPrices,
        recSpecialPrices,
        specialData,
      } = product;

      const isMed = menuType === 'med';

      const prices = isMed ? medicalPrices : recPrices;
      const specialPrices = isMed ? medicalSpecialPrices : recSpecialPrices;
      const usesPosSyncedPricing = Boolean(
        isMed ? posSyncedPricingApplied?.medical : posSyncedPricingApplied?.recreational
      );
      // posBucket.discounts is a pre-existing field on the POSPricingData bucket, written by
      // Armageddon's PricingSyncWriter (bucket_document) alongside conditionDiscounts/rewardDiscounts
      // -- apps/api's applyPOSPrices has just never had a reason to read it before now, so it has no
      // in-repo footprint outside this hook. Each entry's calculationMethod (PERCENT/AMOUNT) is synced
      // from leaflogix via a companion Armageddon change; until that ships and a sync re-runs, entries
      // simply lack calculationMethod, majorityCalculationMethod returns null, and this safely falls
      // back to percent formatting below -- same as any other unresolved calculation method.
      const posBucket = isMed ? POSPricingData?.medical : POSPricingData?.recreational;
      const posCalculationMethod = majorityCalculationMethod(posBucket?.discounts);

      const productSpecialsKeys = getProductSpecialsKeys(product);
      const specialRestrictions = transformSpecialRestrictions({ menuType, specialData, productSpecialsKeys });

      const priceOptionFilter = getPriceOptionFilter({
        isKiosk,
        limitsPerCustomer,
        manualInventory,
        optionsBelowKioskThreshold,
        optionsBelowThreshold,
      });

      const priceOptions = transformOptions({
        options,
        prices,
        product,
        specialPrices,
        specialRestrictions,
        priceOptionFilter,
        usesPosSyncedPricing,
        posCalculationMethod,
      });

      return priceOptions;
    },
    [menuType, isKiosk]
  );

  const getPriceOption = useCallback(
    (product: Product, optionValue: string): PriceOption | null =>
      getPriceOptions(product).find(({ value }) => value === optionValue) ?? null,
    [getPriceOptions]
  );

  return {
    getPriceOption,
    getPriceOptions,
  };
}
